import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as CreditsIcon,
  CheckCircle as CompletedIcon
} from '@mui/icons-material';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',
  orange: '#ffaa01',
  pink: '#df3e99'
};

const MetricCard = ({ title, value, icon: Icon, color, subtitle, loading = false }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card 
      elevation={3}
      sx={{ 
        height: '120px',
        backgroundColor: 'rgba(15, 113, 251, 0.05)',
        border: `1px solid ${color}40`,
        borderRadius: 2,
        color: 'white',
        '&:hover': {
          transform: 'translateY(-2px)',
          transition: 'transform 0.2s ease-in-out',
          backgroundColor: `rgba(15, 113, 251, 0.08)`,
          border: `1px solid ${color}60`
        }
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', height: '100%', p: 2 }}>
        <Box 
          sx={{ 
            bgcolor: `${color}10`, 
            borderRadius: '50%', 
            p: 1.5,
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ color: color, fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={20} sx={{ color: color }} />
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                {value?.toLocaleString() || '0'}
              </Typography>
              {subtitle && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const MetricsCards = ({ data }) => {
  const loading = !data || !data.success;
  const metrics = data?.data || {};

  const formatNumber = (num) => {
    if (!num) return 0;
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  const getTimeframeLabel = () => {
    switch (metrics.timeframe) {
      case '7days': return 'for last 7 days';
      case '30days': return 'for last 30 days';
      case 'today':
      default: return 'for today';
    }
  };


  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        <MetricCard
          title="Total Users"
          value={metrics.totalUsers}
          icon={PeopleIcon}
          color={wyspurColors.blue}
          subtitle="registered accounts"
          loading={loading}
        />
        
        <MetricCard
          title="Active Users"
          value={metrics.activeUsers}
          icon={TrendingUpIcon}
          color={wyspurColors.orange}
          subtitle={getTimeframeLabel()}
          loading={loading}
        />
        
        <MetricCard
          title="Credits Used"
          value={formatNumber(metrics.creditsUsed)}
          icon={CreditsIcon}
          color={wyspurColors.pink}
          subtitle={getTimeframeLabel()}
          loading={loading}
        />
        
        <MetricCard
          title="Completed Jobs"
          value={metrics.completedJobs}
          icon={CompletedIcon}
          color={wyspurColors.blue}
          subtitle={getTimeframeLabel()}
          loading={loading}
        />
      </Grid>
    </Box>
  );
};

export default MetricsCards;